import type { InternalAxiosRequestConfig } from 'axios';
import type { AppDispatch } from '@/store';
import { updateUser } from '@/store/authSlice';
import {
  setOrganizations,
  setCurrentOrg,
  setCurrentOrgId,
  setMembers,
} from '@/store/organizationSlice';
import {
  setVaults,
  setCryptoRates,
  setSigningRequests,
  setTxProposals,
  setPendingSetups,
} from '@/store/vaultSlice';
import { registerDemoFixtureAdapter, clearDemoFixtureAdapter } from '@/api/demoMode';
import {
  demoUser,
  demoOrgWithMembership,
  demoOrgDetail,
  demoMembers,
  demoVaults,
  demoCryptoRates,
  demoSigningRequests,
  demoProposals,
  DEMO_ORG_ID,
  demoVaultData,
} from './demoFixtures';

/**
 * Build the bundled fixture adapter for the apiClient demo firewall. ONLY GET
 * requests reach this (mutations are always hard-rejected — see demoMode.ts).
 * Returns the SSP envelope `{ status: 'success', data }` body, or null to fall
 * through to a DemoModeError. This never touches the network.
 *
 * `config.url` is relative to the apiClient baseURL (`/v1/enterprise`), e.g.
 * `/organizations/demo-org-acme/vaults/demo-vault-eth/balances`.
 */
function buildFixtureAdapter(): (
  config: InternalAxiosRequestConfig
) => Record<string, unknown> | null {
  const ok = (data: Record<string, unknown>) => ({ status: 'success', data });

  return (config) => {
    const rawUrl = config.url ?? '';
    const url = rawUrl.split('?')[0] ?? rawUrl;

    // /organizations/:orgId/vaults  (list)
    const vaultsList = /^\/organizations\/([^/]+)\/vaults$/.exec(url);
    if (vaultsList) {
      return ok({ success: true, vaults: demoVaults });
    }

    // /organizations/:orgId/vaults/:vaultId  (detail)
    const vaultDetail = /^\/organizations\/([^/]+)\/vaults\/([^/]+)$/.exec(url);
    if (vaultDetail) {
      const vid = vaultDetail[2] ?? '';
      const detail = demoVaultData.details[vid];
      if (detail) return ok({ success: true, vault: detail });
      return ok({ success: false, error: 'Vault not found (demo)' });
    }

    // /organizations/:orgId/vaults/:vaultId/<sub>
    const sub = /^\/organizations\/([^/]+)\/vaults\/([^/]+)\/([^/]+)$/.exec(url);
    if (sub) {
      const vid = sub[2] ?? '';
      const leaf = sub[3] ?? '';
      switch (leaf) {
        case 'members':
          return ok({ success: true, members: demoVaultData.members[vid] ?? [] });
        case 'addresses':
          return ok({ success: true, addresses: demoVaultData.addresses[vid] ?? [] });
        case 'balances': {
          const balances = demoVaultData.balances[vid] ?? [];
          const totalUsd = balances.reduce(
            (acc, b) =>
              acc +
              (b.balanceUsd ?? 0) +
              (b.tokens ?? []).reduce((t, tok) => t + (tok.balanceUsd ?? 0), 0),
            0
          );
          return ok({ success: true, balances, totalBalanceUsd: totalUsd });
        }
        case 'transactions': {
          const transactions = demoVaultData.transactions[vid] ?? [];
          return ok({ success: true, transactions, total: transactions.length });
        }
        case 'balance-history':
          return ok({ success: true, history: demoVaultData.history[vid] ?? [] });
        case 'proposals': {
          const proposals = demoVaultData.proposals[vid] ?? [];
          return ok({ success: true, proposals, total: proposals.length });
        }
        case 'policy': {
          const policy = demoVaultData.policy[vid];
          return policy
            ? ok({ success: true, policy })
            : ok({ success: false, error: 'No policy configured (demo)' });
        }
        case 'tokens':
          return ok({ success: true, tokens: [] });
        case 'audit':
          return ok({ success: true, logs: [], total: 0 });
        default:
          return null;
      }
    }

    // /organizations/:orgId/vaults/:vaultId/proposals/:proposalId
    const proposalDetail = /^\/organizations\/([^/]+)\/vaults\/([^/]+)\/proposals\/([^/]+)$/.exec(
      url
    );
    if (proposalDetail) {
      const pid = proposalDetail[3] ?? '';
      const proposal = demoProposals.find((p) => p.id === pid);
      return proposal
        ? ok({ success: true, proposal })
        : ok({ success: false, error: 'Proposal not found (demo)' });
    }

    // /vaults/pending-setup
    if (url === '/vaults/pending-setup') {
      return ok({ success: true, setups: [] });
    }

    // /organizations/:orgId/vault-tags
    if (/^\/organizations\/([^/]+)\/vault-tags$/.exec(url)) {
      return ok({ success: true, tags: [] });
    }

    // /organizations/:orgId/policy  (org default)
    if (/^\/organizations\/([^/]+)\/policy$/.exec(url)) {
      return ok({ success: false, error: 'No org policy (demo)' });
    }

    // No fixture — fall through to hard reject.
    return null;
  };
}

/**
 * Seed all existing Redux slices with the bundled demo fixtures and install the
 * fixture adapter on the apiClient firewall. Called once at demo entry (the
 * /demo route). Pages then read from Redux exactly as in real mode; any
 * fetch-on-mount is served from fixtures or hard-rejected — never the network.
 *
 * This is the "thunk" of DEMO_TREASURY_DESIGN.md §4.3: a single dispatch entry
 * point that hydrates the store. It imports NOTHING from the signing/wallet
 * layer.
 */
export function seedDemoStore(dispatch: AppDispatch): void {
  // Install the network firewall fixture adapter first so any eager fetch is
  // already covered the moment the slices populate.
  registerDemoFixtureAdapter(buildFixtureAdapter());

  // Auth: set the demo user (enterDemoMode already flipped isDemo + cleared
  // any real session). updateUser does NOT set isAuthenticated, so demo stays
  // distinct from real auth.
  dispatch(updateUser(demoUser));

  // Organization
  dispatch(setOrganizations([demoOrgWithMembership]));
  dispatch(setCurrentOrgId(DEMO_ORG_ID));
  dispatch(setCurrentOrg(demoOrgDetail));
  dispatch(setMembers(demoMembers));

  // Vaults + portfolio
  dispatch(setVaults(demoVaults));
  dispatch(setCryptoRates(demoCryptoRates));
  dispatch(setTxProposals(demoProposals));
  dispatch(setSigningRequests(demoSigningRequests));
  dispatch(setPendingSetups([]));

  // Tab-scoped re-entry flag so a mid-demo refresh re-enters the demo instead
  // of bouncing to /login (§10.2). sessionStorage = per-tab + auto-cleared on
  // tab close, never localStorage.
  try {
    sessionStorage.setItem('ssp_demo', '1');
  } catch {
    // Ignore storage errors (private mode etc.) — demo still works for the
    // current navigation, only refresh re-entry is affected.
  }
}

/**
 * Tear down the demo: clear the fixture adapter and the tab-scoped re-entry
 * flag. Redux state is reset by the caller via exitDemoMode()/logout flow.
 */
export function exitDemo(): void {
  clearDemoFixtureAdapter();
  try {
    sessionStorage.removeItem('ssp_demo');
  } catch {
    // Ignore storage errors.
  }
}

/** Whether this tab has an active demo session (for refresh re-entry, §10.2). */
export function hasDemoSession(): boolean {
  try {
    return sessionStorage.getItem('ssp_demo') === '1';
  } catch {
    return false;
  }
}
